import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { scrollToSection } from "@/components/SmoothScroll";
import { Logo } from "@/components/Logo";

const LanguageSwitcher = () => {
  const { lang, setLang } = useLanguage();
  return (
    <div className="relative flex items-center rounded-full border border-black/10 bg-white/60 p-1" data-testid="language-switcher">
      {["ro", "en"].map((l) => (
        <button
          key={l}
          type="button"
          data-testid={`language-${l}`}
          onClick={() => setLang(l)}
          className={`relative rounded-full px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest transition-all duration-300 ${
            lang === l
              ? "bg-[#1A1A1A] text-[#FDFBF7] shadow-sm"
              : "text-[#5C5C5C] hover:text-[#1A1A1A]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const Navbar = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  const go = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header className="fixed inset-x-0 top-4 z-50 px-4 md:top-6">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between rounded-full border border-black/[0.06] bg-white/70 px-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] backdrop-blur-xl md:px-6">
        <button type="button" onClick={() => go("#top")} data-testid="nav-logo-button" aria-label="Iris Languages Academy">
          <Logo />
        </button>

        <nav className="hidden items-center gap-8 md:flex">
          {t.nav.links.map((link) => (
            <button
              key={link.href}
              type="button"
              onClick={() => go(link.href)}
              className="group relative text-sm font-medium text-[#5C5C5C] transition-colors duration-300 hover:text-[#1A1A1A]"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gradient-to-r from-[#FF6B6B] via-[#4ECDC4] to-[#45B7D1] transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-4 md:flex">
          <LanguageSwitcher />
          <motion.button
            type="button"
            data-testid="nav-contact-cta"
            onClick={() => go("#contact")}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            className="rounded-full bg-[#1A1A1A] px-6 py-2.5 text-sm font-medium text-[#FDFBF7] transition-colors duration-300 hover:bg-[#333]"
          >
            {t.nav.cta}
          </motion.button>
        </div>

        <div className="flex items-center gap-3 md:hidden">
          <LanguageSwitcher />
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white/70"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="mx-auto mt-2 flex max-w-6xl flex-col gap-1 rounded-3xl border border-black/[0.06] bg-white/90 p-4 shadow-[0_8px_30px_rgb(0,0,0,0.06)] backdrop-blur-xl md:hidden"
          >
            {t.nav.links.map((link) => (
              <button
                key={link.href}
                type="button"
                onClick={() => go(link.href)}
                className="rounded-2xl px-4 py-3 text-left text-base font-medium text-[#1A1A1A] transition-colors hover:bg-black/[0.04]"
              >
                {link.label}
              </button>
            ))}
            <button
              type="button"
              data-testid="mobile-menu-cta"
              onClick={() => go("#contact")}
              className="mt-2 rounded-2xl bg-[#1A1A1A] px-4 py-3 text-center text-base font-medium text-[#FDFBF7]"
            >
              {t.nav.cta}
            </button>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
